import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../fonts/breuer-headline.css";
import "./question.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const OPTION_ONE = "Myth";
const OPTION_TWO = "Fact";
const MAX_TEXT_LENGTH = 200;
const MAX_TOTAL_QUESTIONS = 20;
const MAX_RETRIES = 3;

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobbyName(locationState) {
  const stateLobby =
    typeof locationState?.lobbyName === "string"
      ? locationState.lobbyName.trim()
      : "";
  if (stateLobby) return stateLobby;
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {}
  return "";
}

function normalizeCorrectAnswer(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "fact") return OPTION_TWO;
  return OPTION_ONE;
}

function sanitizeCountInput(value) {
  return String(value ?? "").replace(/[^\d]/g, "");
}

function normalizeCountOnBlur(value, fallback = 1) {
  const raw = String(value ?? "").trim();
  if (!raw) return String(fallback);
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return String(fallback);
  return String(Math.trunc(n));
}

function parseAiResponse(responseText) {
  const clean = String(responseText || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  if (!clean) throw new Error("AI returned empty response.");

  const tryParse = (candidate) => {
    try {
      return JSON.parse(candidate);
    } catch {
      return null;
    }
  };

  const direct = tryParse(clean);
  if (Array.isArray(direct)) return direct;
  if (Array.isArray(direct?.questions)) return direct.questions;

  const firstBracket = clean.indexOf("[");
  const lastBracket = clean.lastIndexOf("]");
  if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
    const extracted = tryParse(clean.slice(firstBracket, lastBracket + 1));
    if (Array.isArray(extracted)) return extracted;
  }

  throw new Error("AI returned invalid format.");
}

function clampText(value, maxLength = MAX_TEXT_LENGTH) {
  return String(value || "").trim().slice(0, maxLength);
}

function toDisplayQuestion(raw) {
  const questionName = clampText(raw?.question_name);
  const information = clampText(
    raw?.information || raw?.mythpopup || raw?.option_one_popup || raw?.option_two_popup || ""
  );
  const correctAnswer = normalizeCorrectAnswer(raw?.correct_answer);
  return {
    question_name: questionName,
    option_one: OPTION_ONE,
    option_two: OPTION_TWO,
    information,
    option_one_popup: clampText(raw?.option_one_popup || information),
    option_two_popup: clampText(raw?.option_two_popup || information),
    correct_answer: correctAnswer,
    answer_id: correctAnswer === OPTION_ONE ? 1 : 2,
    is_active: "true",
    deleted: false,
  };
}

// ── Prompts: one type at a time so the model can't mix them up ───────────────
function buildSingleTypePrompt(topic, count, type) {
  const answerLabel = type === "Myth" ? "Myth" : "Fact";
  const answerId = type === "Myth" ? 1 : 2;
  return `Return a JSON array of exactly ${count} objects. Topic: "${topic}". All items are ${answerLabel}s. No markdown, no extra text.
Every object must have exactly these fields:
{"question_name":"<declarative statement, no question mark, ≤200 chars>","option_one":"Myth","option_two":"Fact","information":"<one supporting line ≤200 chars>","option_one_popup":"<≤200 chars>","option_two_popup":"<≤200 chars>","correct_answer":"${answerLabel}","answer_id":${answerId}}
Return ONLY the JSON array. Count must be exactly ${count}.`;
}

// ── Single API call ──────────────────────────────────────────────────────────
async function callAI(prompt, token) {
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (!backendBase) throw new Error("Backend URL is not configured.");
  if (!token) throw new Error("Auth token missing.");

  const resp = await fetch(`${backendBase}/generateAi`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");
  return payload?.response;
}

// ── Fetch exactly N items of one type, retry until count matches ─────────────
async function fetchExact(topic, count, type, token) {
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const raw = await callAI(buildSingleTypePrompt(topic, count, type), token);
    const parsed = parseAiResponse(raw);
    // Force the correct_answer on every item — model told it's all one type
    const normalized = parsed
      .map((q) => toDisplayQuestion({ ...q, correct_answer: type }))
      .filter((q) => q.question_name);

    if (normalized.length === count) return normalized;

    // Too many — trim; too few — retry (unless last attempt)
    if (normalized.length > count) return normalized.slice(0, count);
    if (attempt === MAX_RETRIES) {
      if (normalized.length > 0) return normalized; // best effort
      throw new Error(`Could not generate ${count} ${type} items after ${MAX_RETRIES} attempts.`);
    }
  }
}

// ── Generate myths and facts in separate calls then merge ────────────────────
async function generateWithRetry(topic, mythCount, factCount, token) {
  const [myths, facts] = await Promise.all([
    fetchExact(topic, mythCount, "Myth", token),
    fetchExact(topic, factCount, "Fact", token),
  ]);
  return [...myths, ...facts];
}

// ── SVG components ────────────────────────────────────────────────────────────
function QuestionFolderSvg() {
  return (
    <svg className="q-folder-svg" viewBox="0 0 300 240" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden="true">
      <path d="M12,0 H148 L168,36 H288 Q300,36 300,48 V228 Q300,240 288,240 H12 Q0,240 0,228 V12 Q0,0 12,0 Z" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2.5" />
    </svg>
  );
}

function ReviewFolderSvg() {
  return (
    <svg className="q-review-folder-svg" viewBox="0 0 300 240" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden="true">
      <path d="M12,0 H148 L168,36 H288 Q300,36 300,48 V228 Q300,240 288,240 H12 Q0,240 0,228 V12 Q0,0 12,0 Z" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2.5" />
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function Question() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const { status, user } = useSelector((state) => state.auth);

  const [phase, setPhase] = useState("create");
  const [topic, setTopic] = useState("");
  const [mythCount, setMythCount] = useState("5");
  const [factCount, setFactCount] = useState("5");
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegeneratingRemaining, setIsRegeneratingRemaining] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editQuestion, setEditQuestion] = useState("");
  const [editInfo, setEditInfo] = useState("");
  const [editCorrectAnswer, setEditCorrectAnswer] = useState(OPTION_ONE);
  const [targetQuestionCount, setTargetQuestionCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const storedUserData = useMemo(() => readStoredUserData(), []);
  const backendBase = useMemo(() => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""), []);
  const authPayload = useMemo(() => {
    const userId = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const lobby = resolveLobbyName(location.state);
    return { userId, sessionId, organizationId, lobby };
  }, [storedUserData, location.state]);

  const mythCountNumber = Math.max(0, Number(mythCount) || 0);
  const factCountNumber = Math.max(0, Number(factCount) || 0);
  const totalRequested = mythCountNumber + factCountNumber;

  useEffect(() => {
    document.documentElement.classList.add("question-static-bg");
    document.body.classList.add("question-static-bg");
    return () => {
      document.documentElement.classList.remove("question-static-bg");
      document.body.classList.remove("question-static-bg");
    };
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/content"));
  }, [dispatch, status, user]);

  useEffect(() => {
    const alive = questions.filter((q) => !q.deleted);
    if (!alive.length) { setCurrentIndex(0); return; }
    if (currentIndex > alive.length - 1) setCurrentIndex(alive.length - 1);
  }, [questions, currentIndex]);

  useEffect(() => {
    if (!errorMessage.includes("You can only keep up to")) return;
    const timer = window.setTimeout(() => {
      setErrorMessage((prev) => (prev.includes("You can only keep up to") ? "" : prev));
    }, 15000);
    return () => window.clearTimeout(timer);
  }, [errorMessage]);

  const visibleQuestions = useMemo(
    () => questions.map((q, idx) => ({ ...q, _idx: idx })).filter((q) => !q.deleted),
    [questions]
  );
  const currentQuestion = visibleQuestions[currentIndex] || null;
  const isLastVisible = visibleQuestions.length > 0 && currentIndex === visibleQuestions.length - 1;
  const missingQuestionCount = Math.max(0, targetQuestionCount - visibleQuestions.length);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!topic.trim()) return setErrorMessage("Please enter topic name.");
    if (mythCountNumber <= 0 || factCountNumber <= 0) return setErrorMessage("Please enter valid counts for myths and facts.");
    if (totalRequested > MAX_TOTAL_QUESTIONS) return setErrorMessage(`Total questions cannot exceed ${MAX_TOTAL_QUESTIONS}.`);

    setErrorMessage("");
    setSuccessMessage("");
    setIsGenerated(false);
    setIsGenerating(true);
    try {
      const normalized = await generateWithRetry(topic, mythCountNumber, factCountNumber, adminToken);
      if (!normalized?.length) throw new Error("No valid questions were generated.");
      setQuestions(normalized);
      setTargetQuestionCount(normalized.length);
      setCurrentIndex(0);
      setIsEditing(false);
      setIsAdding(false);
      setIsGenerated(true);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to generate questions.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNextToReview = () => {
    if (!questions.length) { setErrorMessage("Please generate questions first."); return; }
    setErrorMessage("");
    setPhase("review");
  };

  const handleRegenerateRemaining = async () => {
    if (!topic.trim()) { setErrorMessage("Topic missing. Please go back and generate again."); return; }
    if (missingQuestionCount <= 0) return;

    const liveMythCount = visibleQuestions.filter((q) => normalizeCorrectAnswer(q.correct_answer) === OPTION_ONE).length;
    const liveFactCount = visibleQuestions.filter((q) => normalizeCorrectAnswer(q.correct_answer) === OPTION_TWO).length;
    let remainingMyths = Math.max(0, mythCountNumber - liveMythCount);
    let remainingFacts = Math.max(0, factCountNumber - liveFactCount);
    const computedMissing = remainingMyths + remainingFacts;
    if (computedMissing !== missingQuestionCount) {
      remainingMyths = Math.min(remainingMyths, missingQuestionCount);
      remainingFacts = Math.max(0, missingQuestionCount - remainingMyths);
    }
    if (remainingMyths + remainingFacts <= 0) { setErrorMessage("No remaining questions to regenerate."); return; }

    setErrorMessage("");
    setSuccessMessage("");
    setIsRegeneratingRemaining(true);
    try {
      // Use split-call approach for exact counts
      const parts = await Promise.all([
        remainingMyths > 0 ? fetchExact(topic, remainingMyths, "Myth", adminToken) : Promise.resolve([]),
        remainingFacts > 0 ? fetchExact(topic, remainingFacts, "Fact", adminToken) : Promise.resolve([]),
      ]);
      const normalized = [...parts[0], ...parts[1]];
      if (!normalized.length) throw new Error("No valid questions were generated.");
      setQuestions((prev) => [...prev, ...normalized]);
      if (!visibleQuestions.length) setCurrentIndex(0);
      setSuccessMessage(`Regenerated ${normalized.length} question(s).`);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to regenerate questions.");
    } finally {
      setIsRegeneratingRemaining(false);
    }
  };

  const handlePrev = () => { if (currentIndex <= 0) return; setCurrentIndex((p) => p - 1); setIsEditing(false); };
  const handleNext = () => { if (currentIndex >= visibleQuestions.length - 1) return; setCurrentIndex((p) => p + 1); setIsEditing(false); };

  const startEdit = () => {
    if (!currentQuestion) return;
    setEditQuestion(currentQuestion.question_name);
    setEditInfo(currentQuestion.information || "");
    setEditCorrectAnswer(currentQuestion.correct_answer || OPTION_ONE);
    setIsAdding(false);
    setIsEditing(true);
  };

  const saveEdit = () => {
    const nextCorrect = normalizeCorrectAnswer(editCorrectAnswer);
    const nextQuestionName = clampText(editQuestion);
    const nextInfo = clampText(editInfo);
    if (!nextQuestionName) { setErrorMessage("Question cannot be empty."); return; }
    setErrorMessage("");

    if (isAdding) {
      if (visibleQuestions.length >= MAX_TOTAL_QUESTIONS) {
        setErrorMessage(`You can only keep up to ${MAX_TOTAL_QUESTIONS} total questions.`);
        return;
      }
      setQuestions((prev) => [
        ...prev,
        {
          question_name: nextQuestionName,
          option_one: OPTION_ONE,
          option_two: OPTION_TWO,
          information: nextInfo,
          option_one_popup: nextInfo,
          option_two_popup: nextInfo,
          correct_answer: nextCorrect,
          answer_id: nextCorrect === OPTION_ONE ? 1 : 2,
          is_active: "true",
          deleted: false,
        },
      ]);
      setCurrentIndex(visibleQuestions.length);
      setIsEditing(false);
      setIsAdding(false);
      return;
    }

    if (!currentQuestion) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx === currentQuestion._idx
          ? { ...q, question_name: nextQuestionName, information: nextInfo, option_one_popup: nextInfo, option_two_popup: nextInfo, correct_answer: nextCorrect, answer_id: nextCorrect === OPTION_ONE ? 1 : 2 }
          : q
      )
    );
    setIsEditing(false);
    setIsAdding(false);
  };

  const handleDelete = async () => {
    if (!currentQuestion) return;
    const result = await Swal.fire({ title: "Are you sure?", text: "Do you want to delete this question?", icon: "warning", showCancelButton: true, confirmButtonText: "OK", cancelButtonText: "Cancel", confirmButtonColor: "#d33" });
    if (!result.isConfirmed) return;
    setQuestions((prev) => prev.map((q, idx) => idx === currentQuestion._idx ? { ...q, deleted: true } : q));
    setIsEditing(false);
    setIsAdding(false);
  };

  const startAdd = () => {
    if (visibleQuestions.length >= MAX_TOTAL_QUESTIONS) { setErrorMessage(`You can only keep up to ${MAX_TOTAL_QUESTIONS} total questions.`); return; }
    setErrorMessage("");
    setEditQuestion("");
    setEditInfo("");
    setEditCorrectAnswer(OPTION_ONE);
    setIsAdding(true);
    setIsEditing(true);
  };

  const handlePublish = async () => {
    if (!backendBase || !adminToken) { setErrorMessage("Backend URL or token missing."); return; }
    const ready = visibleQuestions;
    if (!ready.length) { setErrorMessage("No questions to publish."); return; }

    setIsPublishing(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const { userId, sessionId, organizationId, lobby } = authPayload;
      const resp = await fetch(`${backendBase}/addAIQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId, sessionId, organizationId, lobby,
          questions: ready.map((q) => ({
            question_name: q.question_name,
            option_one: OPTION_ONE,
            option_two: OPTION_TWO,
            information: q.information || "",
            option_one_popup: q.option_one_popup || q.information || "",
            option_two_popup: q.option_two_popup || q.information || "",
            answer_id: q.answer_id,
            correct_answer: q.correct_answer,
            is_active: "true",
          })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) throw new Error(data?.message || "Failed to publish questions.");

      setSuccessMessage(`Published ${data.inserted ?? ready.length} questions successfully.`);
      const randomPin = Math.floor(1000 + Math.random() * 9000);
      const lobbyLabel = authPayload.lobby || "main";
      const shareBase = String(process.env.REACT_APP_BASE_URL || window.location.origin || "").replace(/\/+$/, "");
      const joinLink = `${shareBase}/game?pin=${randomPin}&lobby=${encodeURIComponent(lobbyLabel)}`;
      navigate("/host", { replace: true, state: { pin: randomPin, link: joinLink, lobbyName: lobbyLabel } });
      setPhase("create");
      setTopic("");
      setMythCount("5");
      setFactCount("5");
      setQuestions([]);
      setTargetQuestionCount(0);
      setCurrentIndex(0);
      setIsEditing(false);
      setIsAdding(false);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to publish questions.");
    } finally {
      setIsPublishing(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="question-page">
      <div className="question-stack">
        {phase === "create" ? (
          <>
            <div className="q-folder">
              <QuestionFolderSvg />
              <span className="q-folder-tab-text">Create with AI</span>
              <div className="q-folder-card">
                <input
                  className="q-line-input"
                  type="text"
                  value={topic}
                  onChange={(e) => { setTopic(e.target.value); setErrorMessage(""); setIsGenerated(false); }}
                  placeholder="Topic Name"
                  disabled={isGenerating}
                />
                <div className="q-count-row">
                  <div className="q-count-col">
                    <label className="q-count-label" htmlFor="mythCountInput">No. of myths</label>
                    <input
                      id="mythCountInput"
                      className="q-line-input"
                      type="text"
                      inputMode="numeric"
                      value={mythCount}
                      onChange={(e) => {
                        const raw = sanitizeCountInput(e.target.value);
                        if (!raw) { setMythCount(""); setIsGenerated(false); setErrorMessage(""); return; }
                        const parsed = Math.max(0, Number(raw) || 0);
                        setMythCount(String(Math.min(parsed, Math.max(0, MAX_TOTAL_QUESTIONS - factCountNumber))));
                        setIsGenerated(false);
                        setErrorMessage("");
                      }}
                      onBlur={() => setMythCount((prev) => normalizeCountOnBlur(prev, 1))}
                      placeholder="No. of myths"
                      disabled={isGenerating}
                    />
                  </div>
                  <div className="q-count-col">
                    <label className="q-count-label" htmlFor="factCountInput">No. of facts</label>
                    <input
                      id="factCountInput"
                      className="q-line-input"
                      type="text"
                      inputMode="numeric"
                      value={factCount}
                      onChange={(e) => {
                        const raw = sanitizeCountInput(e.target.value);
                        if (!raw) { setFactCount(""); setIsGenerated(false); setErrorMessage(""); return; }
                        const parsed = Math.max(0, Number(raw) || 0);
                        setFactCount(String(Math.min(parsed, Math.max(0, MAX_TOTAL_QUESTIONS - mythCountNumber))));
                        setIsGenerated(false);
                        setErrorMessage("");
                      }}
                      onBlur={() => setFactCount((prev) => normalizeCountOnBlur(prev, 1))}
                      placeholder="No. of facts"
                      disabled={isGenerating}
                    />
                  </div>
                </div>
                <button type="button" className="q-generate-btn" onClick={handleGenerate} disabled={isGenerating}>
                  {isGenerating ? "Generating..." : isGenerated ? "Generated" : "Generate with AI"}
                </button>
              </div>
            </div>
            <button type="button" className="q-next-main-btn" onClick={handleNextToReview} disabled={isGenerating}>
              Next
            </button>
          </>
        ) : (
          <div className="q-frame review">
            <ReviewFolderSvg />
            <span className="q-review-folder-tab">
              {currentQuestion ? currentQuestion.correct_answer.toUpperCase() : "PREVIEW"}
            </span>
            <button type="button" className="q-arrow left" onClick={handlePrev} disabled={currentIndex <= 0} aria-label="Previous question">&#10094;</button>
            <button type="button" className="q-arrow right" onClick={handleNext} disabled={currentIndex >= visibleQuestions.length - 1} aria-label="Next question">&#10095;</button>
            {currentQuestion ? (
              <div className="q-review-body">
                {!isEditing ? (
                  <>
                    <p className="q-review-question">"{currentQuestion.question_name}"</p>
                    <p className="q-review-info"><strong>info-</strong> "{currentQuestion.information}"</p>
                  </>
                ) : (
                  <div className="q-edit-panel">
                    <input className="q-line-input" value={editQuestion} onChange={(e) => setEditQuestion(e.target.value)} placeholder="Question" maxLength={MAX_TEXT_LENGTH} />
                    <input className="q-line-input" value={editInfo} onChange={(e) => setEditInfo(e.target.value)} placeholder="Information" maxLength={MAX_TEXT_LENGTH} />
                    <select className="q-line-input" value={editCorrectAnswer} onChange={(e) => setEditCorrectAnswer(e.target.value)}>
                      <option value={OPTION_ONE}>Myth</option>
                      <option value={OPTION_TWO}>Fact</option>
                    </select>
                  </div>
                )}
                <div className="q-action-row">
                  {!isEditing ? (
                    <>
                      <button type="button" className="q-mini-btn" onClick={startEdit}>Edit</button>
                      <button type="button" className="q-mini-btn danger" onClick={handleDelete}>Delete</button>
                      <button type="button" className="q-mini-btn" onClick={startAdd}>Add</button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="q-mini-btn" onClick={saveEdit}>Save</button>
                      <button type="button" className="q-mini-btn" onClick={() => { setIsEditing(false); setIsAdding(false); }}>Cancel</button>
                    </>
                  )}
                </div>
                <div className="q-counter-box">{currentIndex + 1} out of {visibleQuestions.length}</div>
                {missingQuestionCount > 0 && !isEditing ? (
                  <button type="button" className="q-mini-btn" onClick={handleRegenerateRemaining} disabled={isRegeneratingRemaining}>
                    {isRegeneratingRemaining ? "Regenerating..." : `Regenerate remaining ${missingQuestionCount}`}
                  </button>
                ) : null}
                {isLastVisible ? (
                  <button type="button" className="q-publish-btn" onClick={handlePublish} disabled={isPublishing}>
                    {isPublishing ? "Publishing..." : "Publish"}
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="q-review-body">
                <p className="q-review-info">No questions left. Go back and generate again.</p>
                {missingQuestionCount > 0 ? (
                  <button type="button" className="q-mini-btn" onClick={handleRegenerateRemaining} disabled={isRegeneratingRemaining}>
                    {isRegeneratingRemaining ? "Regenerating..." : `Regenerate remaining ${missingQuestionCount}`}
                  </button>
                ) : null}
                <button type="button" className="q-mini-btn" onClick={() => setPhase("create")}>Back</button>
              </div>
            )}
          </div>
        )}
        {errorMessage ? <p className="q-error">{errorMessage}</p> : null}
        {successMessage ? <p className="q-success">{successMessage}</p> : null}
      </div>
    </div>
  );
}

export default Question;
